import { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { NumberPagination } from "./index";

/**
 * Reusable Slider Component
 *
 * @param {Array} items - Array of React elements/children to display as slides
 * @param {number} itemsPerView - Number of items visible at once on desktop (default: 3)
 * @param {number} mobileItemsPerView - Number of items visible at once on mobile, can be fractional (default: itemsPerView)
 * @param {number} lines - Number of rows/lines for grid layout on desktop (default: 1)
 * @param {number} mobileLines - Number of rows/lines for grid layout on mobile (default: lines)
 * @param {string} navigationType - "next-prev" or "number" (default: "next-prev")
 * @param {string} navigatorPosition - "center", "left", or "right" (default: "center")
 * @param {number} gap - Gap size multiplier (gap-5 = 1.25rem, default: 5)
 * @param {string} className - Additional classes for container
 * @param {string} containerClassName - Additional classes for slider container
 * @param {function} onSlideChange - Callback when slide changes (receives new index)
 *
 * Features:
 * - Desktop: next-prev moves one item, number navigation uses pagination
 * - Mobile: mobileItemsPerView items at first (supports fractional like 1.2), free grab movement
 * - All items maintain consistent width and height
 * - Supports touch and mouse drag on mobile with completely free movement
 */
const Slider = ({
  items = [],
  itemsPerView = 3,
  mobileItemsPerView, // Optional: if not provided, uses itemsPerView
  lines = 1,
  mobileLines, // Optional: if not provided, uses lines
  navigationType = "next-prev", // "next-prev" or "number"
  navigatorPosition = "center", // "center", "left", "right"
  gap = 5, // gap size in rem (default 5 = 1.25rem)
  className = "",
  containerClassName = "",
  onSlideChange,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [totalDragOffset, setTotalDragOffset] = useState(0); // Track cumulative drag for free movement
  const containerRef = useRef(null);
  const dragStartRef = useRef(0);
  const gapSize = gap * 0.25; // Convert gap number to rem (gap-5 = 1.25rem)

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Use mobileItemsPerView if provided, otherwise use itemsPerView
  const effectiveMobileItemsPerView = mobileItemsPerView ?? itemsPerView;
  // Use mobileLines if provided, otherwise use lines
  const effectiveMobileLines = mobileLines ?? lines;

  // Calculate items per page based on itemsPerView and lines
  // Desktop: itemsPerView * lines
  // Mobile: effectiveMobileItemsPerView * effectiveMobileLines
  const desktopItemsPerPage = itemsPerView * lines;
  const mobileItemsPerPage = effectiveMobileItemsPerView * effectiveMobileLines;
  const itemsPerPage = isMobile ? mobileItemsPerPage : desktopItemsPerPage;
  const totalItems = items.length;

  // For number navigation: calculate pages based on current device
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Desktop item width calculation
  const desktopItemWidth = `calc((100% - ${
    (itemsPerView - 1) * gapSize
  }rem) / ${itemsPerView})`;

  // Mobile: use mobileItemsPerView (can be fractional like 1.2)
  // Calculate gaps: if showing 1.2 items, we have 1 full gap
  const mobileGaps = Math.floor(effectiveMobileItemsPerView);
  const mobileItemWidth = `calc((100% - ${
    mobileGaps * gapSize
  }rem) / ${effectiveMobileItemsPerView})`;

  // Navigation handlers
  const handleNext = () => {
    if (navigationType === "next-prev") {
      // Desktop: move one item
      if (!isMobile) {
        const maxIndex = Math.max(0, totalItems - itemsPerView);
        if (currentIndex < maxIndex) {
          const newIndex = currentIndex + 1;
          setCurrentIndex(newIndex);
          onSlideChange?.(newIndex);
        }
      } else {
        // Mobile: move by effectiveMobileItemsPerView (can be fractional)
        const maxIndex = Math.max(
          0,
          totalItems - Math.ceil(effectiveMobileItemsPerView)
        );
        if (currentIndex < maxIndex) {
          const newIndex = Math.min(
            currentIndex + Math.ceil(effectiveMobileItemsPerView),
            maxIndex
          );
          setCurrentIndex(newIndex);
          setTotalDragOffset(0); // Reset drag offset when using buttons
          onSlideChange?.(newIndex);
        }
      }
    } else {
      // Number navigation: move to next page
      if (currentPage < totalPages) {
        const newPage = currentPage + 1;
        setCurrentPage(newPage);
        const newIndex = (newPage - 1) * itemsPerPage;
        setCurrentIndex(newIndex);
        onSlideChange?.(newIndex);
      }
    }
  };

  const handlePrev = () => {
    if (navigationType === "next-prev") {
      // Desktop: move one item back
      if (!isMobile) {
        if (currentIndex > 0) {
          const newIndex = currentIndex - 1;
          setCurrentIndex(newIndex);
          onSlideChange?.(newIndex);
        }
      } else {
        // Mobile: move back by effectiveMobileItemsPerView (can be fractional)
        if (currentIndex > 0) {
          const newIndex = Math.max(
            0,
            currentIndex - Math.ceil(effectiveMobileItemsPerView)
          );
          setCurrentIndex(newIndex);
          setTotalDragOffset(0); // Reset drag offset when using buttons
          onSlideChange?.(newIndex);
        }
      }
    } else {
      // Number navigation: move to previous page
      if (currentPage > 1) {
        const newPage = currentPage - 1;
        setCurrentPage(newPage);
        const newIndex = (newPage - 1) * itemsPerPage;
        setCurrentIndex(newIndex);
        onSlideChange?.(newIndex);
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const newIndex = (page - 1) * itemsPerPage;
    setCurrentIndex(newIndex);
    onSlideChange?.(newIndex);
  };

  // Build transform for desktop
  const buildDesktopTransform = () => {
    if (navigationType === "number") {
      // Number navigation: show items for current page
      return "translateX(0)";
    } else {
      // Next-prev: move by item width
      if (currentIndex === 0) return "translateX(0)";
      const itemMove = `calc(${desktopItemWidth} + ${gapSize}rem)`;
      return `translateX(calc(-1 * ${currentIndex} * (${itemMove})))`;
    }
  };

  // Build transform for mobile
  const buildMobileTransform = () => {
    if (isDragging) {
      // During drag: completely free movement - use cumulative offset
      const basePosition = totalDragOffset;
      return `translateX(${basePosition + dragOffset}px)`;
    }

    // Normal state: show mobileItemsPerView items from currentIndex
    // For fractional views, calculate based on item width
    if (currentIndex === 0 && totalDragOffset === 0) return "translateX(0)";

    // Calculate base position from currentIndex
    const itemMove = `calc(${mobileItemWidth} + ${gapSize}rem)`;
    const baseTransform =
      currentIndex === 0 ? "0px" : `calc(-1 * ${currentIndex} * (${itemMove}))`;

    // Add any remaining drag offset for smooth transitions
    if (totalDragOffset !== 0) {
      return `translateX(calc(${baseTransform} + ${totalDragOffset}px))`;
    }
    return `translateX(${baseTransform})`;
  };

  const buildTransform = () => {
    if (isMobile) {
      return buildMobileTransform();
    } else {
      return buildDesktopTransform();
    }
  };

  // Get items to display
  const getDisplayItems = () => {
    if (navigationType === "number") {
      // Number navigation: show items for current page (both desktop and mobile)
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return items.slice(startIndex, endIndex);
    } else {
      // Next-prev: show all items, use transform to position
      return items;
    }
  };

  // Touch/Drag handlers for mobile - completely free movement
  const handleTouchStart = (e) => {
    if (!isMobile || navigationType === "number") return;
    setIsDragging(true);
    dragStartRef.current = e.touches[0].clientX;
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !isDragging || navigationType === "number") return;
    e.preventDefault();
    const currentX = e.touches[0].clientX;
    const diff = currentX - dragStartRef.current;
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging || navigationType === "number") return;
    setIsDragging(false);

    // Free movement - just accumulate the drag offset, no snapping
    // Update totalDragOffset to maintain the position
    setTotalDragOffset((prev) => prev + dragOffset);

    // Update currentIndex based on approximate position for navigation buttons
    if (containerRef.current && Math.abs(dragOffset) > 10) {
      const containerWidth = containerRef.current.offsetWidth;
      const itemWidthPx =
        containerWidth / effectiveMobileItemsPerView - gapSize * 16;
      const moveDistance = itemWidthPx + gapSize * 16;
      const totalOffset = totalDragOffset + dragOffset;
      const itemsMoved = Math.round(totalOffset / moveDistance);
      const newIndex = Math.max(
        0,
        Math.min(
          totalItems - Math.ceil(effectiveMobileItemsPerView),
          itemsMoved
        )
      );

      if (newIndex !== currentIndex) {
        setCurrentIndex(newIndex);
        // Reset totalDragOffset when index changes significantly
        setTotalDragOffset(0);
        onSlideChange?.(newIndex);
      }
    }

    setDragOffset(0);
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (!isMobile || navigationType === "number") return;
    setIsDragging(true);
    dragStartRef.current = e.clientX;
    setDragOffset(0);
  };

  useEffect(() => {
    if (!isDragging || !isMobile || navigationType === "number") return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - dragStartRef.current;
      setDragOffset(diff);
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      // Free movement - just accumulate the drag offset, no snapping
      setTotalDragOffset((prev) => prev + dragOffset);

      // Update currentIndex based on approximate position for navigation buttons
      if (containerRef.current && Math.abs(dragOffset) > 10) {
        const containerWidth = containerRef.current.offsetWidth;
        const itemWidthPx =
          containerWidth / effectiveMobileItemsPerView - gapSize * 16;
        const moveDistance = itemWidthPx + gapSize * 16;
        const totalOffset = totalDragOffset + dragOffset;
        const itemsMoved = Math.round(totalOffset / moveDistance);
        const newIndex = Math.max(
          0,
          Math.min(
            totalItems - Math.ceil(effectiveMobileItemsPerView),
            itemsMoved
          )
        );

        if (newIndex !== currentIndex) {
          setCurrentIndex(newIndex);
          setTotalDragOffset(0);
          onSlideChange?.(newIndex);
        }
      }

      setDragOffset(0);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDragging,
    isMobile,
    navigationType,
    currentIndex,
    dragOffset,
    totalDragOffset,
    effectiveMobileItemsPerView,
    totalItems,
    gapSize,
    onSlideChange,
  ]);

  // Animation state for number navigation
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (navigationType === "number") {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentPage, navigationType]);

  // Get item width
  const getItemWidth = () => {
    return isMobile ? mobileItemWidth : desktopItemWidth;
  };

  // Check if can go next/prev
  const canGoNext = () => {
    if (navigationType === "next-prev") {
      if (!isMobile) {
        return currentIndex < Math.max(0, totalItems - itemsPerView);
      } else {
        return (
          currentIndex <
          Math.max(0, totalItems - Math.ceil(effectiveMobileItemsPerView))
        );
      }
    } else {
      return currentPage < totalPages;
    }
  };

  const canGoPrev = () => {
    if (navigationType === "next-prev") {
      return currentIndex > 0;
    } else {
      return currentPage > 1;
    }
  };

  const displayItems = getDisplayItems();
  const gapRem = `${gapSize}rem`;

  return (
    <div className={`w-full ${className}`}>
      {/* Slider Container */}
      <div className={`w-full overflow-hidden ${containerClassName}`}>
        {navigationType === "number" ? (
          // Number navigation: grid layout with pagination (desktop and mobile)
          <div
            className="w-full grid"
            style={{
              gridTemplateColumns: `repeat(${
                isMobile ? Math.ceil(effectiveMobileItemsPerView) : itemsPerView
              }, 1fr)`,
              gridTemplateRows:
                (isMobile ? effectiveMobileLines : lines) > 1
                  ? `repeat(${isMobile ? effectiveMobileLines : lines}, 1fr)`
                  : "auto",
              gap: gapRem,
              opacity: isAnimating ? 0 : 1,
              transform: isAnimating ? "translateY(10px)" : "translateY(0)",
              transition:
                "opacity 0.5s ease-in-out, transform 0.5s ease-in-out",
            }}
          >
            {displayItems.map((item, index) => (
              <div
                key={`${currentPage}-${index}`}
                className="flex-shrink-0 w-full"
                style={{
                  minHeight:
                    (isMobile ? effectiveMobileLines : lines) > 1
                      ? "auto"
                      : "100%",
                }}
              >
                <div className="w-full h-full">{item}</div>
              </div>
            ))}
          </div>
        ) : (
          // Next-prev or mobile: flex layout with transform
          <div
            ref={containerRef}
            className={`flex ${
              !isDragging ? "transition-transform duration-500 ease-out" : ""
            }`}
            style={{
              gap: gapRem,
              transform: buildTransform(),
              cursor:
                isMobile && navigationType === "next-prev"
                  ? isDragging
                    ? "grabbing"
                    : "grab"
                  : "default",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          >
            {items.map((item, index) => (
              <div
                key={index}
                className="flex-shrink-0"
                style={{
                  width: getItemWidth(),
                  minWidth: getItemWidth(),
                  maxWidth: getItemWidth(),
                  height: "100%",
                }}
              >
                <div className="w-full h-full">{item}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      {navigationType === "next-prev" && (
        <div
          className={`flex flex-row items-center justify-${
            navigatorPosition === "center"
              ? "center"
              : navigatorPosition === "left"
              ? "start"
              : "end"
          } gap-[15px] mt-5`}
        >
          <button
            onClick={handlePrev}
            disabled={!canGoPrev()}
            className="w-[35px] h-[35px] border border-[#0000004D] rounded-full flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-gray-50"
          >
            <Icon icon="mdi:chevron-left" className="text-lg" />
          </button>
          <button
            onClick={handleNext}
            disabled={!canGoNext()}
            className="w-[35px] h-[35px] border border-[#0000004D] rounded-full flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-gray-50"
          >
            <Icon icon="mdi:chevron-right" className="text-lg" />
          </button>
        </div>
      )}

      {navigationType === "number" && totalPages > 1 && (
        <NumberPagination
          length={totalPages}
          actived={currentPage}
          onPageChange={handlePageChange}
          position={navigatorPosition}
          className="mt-5"
        />
      )}
    </div>
  );
};

export default Slider;
